import { ERROR_MESSAGES, VALIDATION_RULES } from './constants';

// 日付をフォーマット（定数参照）
export const formatDate = (date, formatKey = 'FULL') => {
  if (!date) return '';
  
  const dateObj = date.toDate ? date.toDate() : new Date(date);
  
  if (isNaN(dateObj.getTime())) return '';
  
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  const hours = String(dateObj.getHours()).padStart(2, '0');
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');
  
  const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
  const weekday = weekdays[dateObj.getDay()];
  
  switch (formatKey) {
    case 'FULL':
      return `${year}年${month}月${day}日(${weekday})`;
    case 'SHORT':
      return `${year}/${month}/${day}`;
    case 'TIME':
      return `${hours}:${minutes}`;
    case 'DATETIME':
      return `${year}年${month}月${day}日(${weekday}) ${hours}:${minutes}`;
    case 'ISO':
      return `${year}-${month}-${day}`;
    default:
      return `${year}年${month}月${day}日`;
  }
};

// メールアドレスのバリデーション（統一された戻り値）
export const validateEmail = (email) => {
  const isValid = VALIDATION_RULES.EMAIL_REGEX.test(email);
  return {
    isValid,
    message: isValid ? '' : '有効なメールアドレスを入力してください。'
  };
};

// パスワードのバリデーション
export const validatePassword = (password) => {
  if (!password || password.length < VALIDATION_RULES.PASSWORD_MIN_LENGTH) {
    return {
      isValid: false,
      message: `パスワードは${VALIDATION_RULES.PASSWORD_MIN_LENGTH}文字以上で入力してください。`
    };
  }
  
  return { isValid: true };
};

// 必須フィールドのバリデーション
export const validateRequired = (value, fieldName) => {
  if (!value || value.trim() === '') {
    return {
      isValid: false,
      message: `${fieldName}は必須です。`
    };
  }
  
  return { isValid: true };
};

// 文字数のバリデーション
export const validateMaxLength = (value, maxLength, fieldName) => {
  if (value && value.length > maxLength) {
    return {
      isValid: false,
      message: `${fieldName}は${maxLength}文字以内で入力してください。`
    };
  }
  
  return { isValid: true };
};

// 参加人数のバリデーション
export const validateParticipants = (maxParticipants) => {
  const num = parseInt(maxParticipants);
  if (isNaN(num) || num < 1) {
    return { 
      isValid: false, 
      message: '参加人数は1人以上で指定してください。' 
    };
  }
  if (num > VALIDATION_RULES.MAX_PARTICIPANTS) {
    return { 
      isValid: false, 
      message: `参加人数は${VALIDATION_RULES.MAX_PARTICIPANTS}人以下で指定してください。` 
    };
  }
  return { isValid: true };
};

// URLのバリデーション
export const validateUrl = (url) => {
  if (!url) return { isValid: true }; // 空の場合は有効とする
  
  try {
    new URL(url);
    return { isValid: true };
  } catch {
    return {
      isValid: false,
      message: '有効なURLを入力してください。'
    };
  }
};

// エラーメッセージの取得（網羅的なエラーハンドリング）
export const getErrorMessage = (error) => {
  if (!error) return ERROR_MESSAGES.SERVER_ERROR;
  
  const errorCode = error.code || error.message;
  
  switch (errorCode) {
    case 'auth/user-not-found':
    case 'auth/wrong-password':
      return ERROR_MESSAGES.LOGIN_FAILED;
    case 'auth/email-already-in-use':
      return 'このメールアドレスは既に使用されています。';
    case 'auth/weak-password':
      return 'パスワードが弱すぎます。より強力なパスワードを設定してください。';
    case 'auth/invalid-email':
      return '有効なメールアドレスを入力してください。';
    case 'auth/too-many-requests':
      return 'リクエストが多すぎます。しばらくしてからもう一度お試しください。';
    case 'auth/network-request-failed':
      return ERROR_MESSAGES.NETWORK_ERROR;
    case 'auth/user-cancelled':
      return '操作がキャンセルされました。';
    case 'auth/user-disabled':
      return 'このアカウントは無効化されています。';
    case 'auth/requires-recent-login':
      return 'セキュリティのため、再度ログインしてください。';
    case 'permission-denied':
      return ERROR_MESSAGES.FORBIDDEN;
    case 'unauthenticated':
      return ERROR_MESSAGES.UNAUTHORIZED;
    case 'not-found':
      return ERROR_MESSAGES.NOT_FOUND;
    case 'deadline-exceeded':
      return 'リクエストがタイムアウトしました。';
    case 'unavailable':
      return 'サービスが一時的に利用できません。';
    default:
      return error.message || ERROR_MESSAGES.SERVER_ERROR;
  }
};

// デバウンス関数
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// スロットル関数
export const throttle = (func, limit) => {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

// オブジェクトの深いコピー（モダンなAPI使用）
export const deepClone = (obj) => {
  if (!obj) return obj;
  return structuredClone(obj); // 標準APIを利用
};

// クエリパラメータの取得
export const getQueryParams = (url) => {
  const params = new URLSearchParams(url || window.location.search);
  const result = {};
  for (const [key, value] of params) {
    result[key] = value;
  }
  return result;
};

// 金額のフォーマット
export const formatCurrency = (amount, currency = 'JPY') => {
  return new Intl.NumberFormat('ja-JP', {
    style: 'currency',
    currency: currency
  }).format(amount);
};

// テキストのトリミング（サロゲートペア対応）
export const truncateText = (text, maxLength) => {
  if (!text) return '';
  const chars = Array.from(text); // 絵文字を1文字として扱う
  if (chars.length <= maxLength) return text;
  return chars.slice(0, maxLength).join('') + '...';
};
